import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ReactNode } from 'react';
import { useGetPublicLocationQuery } from '../../services/endpoints/places/location';
import { useGetCoursePublicQuery } from '../../services/endpoints/schedule/course';

const SESSION_TYPE_MAP: Record<string, string> = {
    'school-programs': 'after_school',
    'after-school': 'after_school',
    'cooking-camps': 'camps',
    camps: 'camps',
    events: 'events'
};

// Old location.slug values -> current franchise microsite slugs
export const FRANCHISE_SLUG_REDIRECTS: Record<string, string> = {
    fontana: 'ie_west',
    irvine: 'orange_county',
    brea: 'orange_county_north',
    houston: 'houston_west',
    sammamish: 'seattle_eastside',
    phoenix: 'phoenix_north',
    jacksonville: 'northeastern_florida',
    dallas: 'dallas_north'
};

// /franchise/{old_slug} -> /franchise/{new_slug}
export const FranchiseSlugRedirect = ({ children }: { children: ReactNode }) => {
    const { slug } = useParams();
    const nextSlug = slug ? FRANCHISE_SLUG_REDIRECTS[slug] : undefined;
    if (nextSlug) {
        return <Navigate to={`/franchise/${nextSlug}`} replace />;
    }
    return <>{children}</>;
};

// v2 /{school-programs|camps}/{location}/{slug} -> v3 /sessions/{slug}
export const LegacySessionRedirect = () => {
    const { slug } = useParams();
    return <Navigate to={`/sessions/${slug}`} replace />;
};

// v2 /pages/{slug} -> v3 /sticky-notes/{slug}
export const LegacyPageRedirect = () => {
    const { slug } = useParams();
    return <Navigate to={`/sticky-notes/${slug}`} replace />;
};

// v2 /admin/sessions/{slug} -> v3 /admin/schedule/sessions/{id}
export const LegacyAdminSessionRedirect = () => {
    const { slug } = useParams();
    const { data, isLoading, isError } = useGetCoursePublicQuery(slug!, { skip: !slug });

    if (isLoading) return null;
    if (isError || !data?.course?.id) return <Navigate to="/admin/schedule/sessions" replace />;

    return <Navigate to={`/admin/schedule/sessions/${data.course.id}`} replace />;
};

// v2 /courses?... -> v3 /sessions?... (or a location's microsite)
export const LegacyCoursesRedirect = () => {
    const [params] = useSearchParams();
    const programTypes = params.get('program_types') ?? '';
    const sessionTypes = params.get('session_types') ?? '';
    const locations = params.get('locations') ?? '';
    const venueId = params.get('venue_id') ?? '';

    if (programTypes === 'online') {
        return <Navigate to="/sessions?session_type=online" replace />;
    }

    return (
        <SessionSearchRedirect
            sessionType={SESSION_TYPE_MAP[sessionTypes]}
            locationId={locations}
            venueId={venueId}
        />
    );
};

const SessionSearchRedirect = ({
                                   sessionType,
                                   locationId,
                                   venueId
                               }: {
    sessionType?: string;
    locationId: string;
    venueId: string;
}) => {
    const { data, isLoading } = useGetPublicLocationQuery(Number(locationId), { skip: !locationId });
    if (locationId && isLoading) return null;

    const next = new URLSearchParams();
    if (sessionType) next.set('session_type', sessionType);
    if (venueId) next.set('venue_id', venueId);

    const loc = data?.location;
    if (loc?.latitude != null && loc?.longitude != null) {
        next.set('origin', `${loc.latitude}_${loc.longitude}`);
    } else if (locationId) {
        next.set('location_id', locationId);
    }

    const qs = next.toString();
    return <Navigate to={`/sessions${qs ? `?${qs}` : ''}`} replace />;
};
